'use client';

import { ShieldCheck } from '@phosphor-icons/react';
import { LegalPageLayout } from '@/components/legal/legal-page-layout';

const BulletList = ({ items }: { items: string[] }) => (
  <ul className="space-y-1.5 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="text-[var(--primary)] mt-1.5 w-1 h-1 rounded-pill bg-[var(--primary)] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export function PrivacyContent() {
  const sections = [
    {
      title: '1. Общие положения',
      content: (
        <>
          <p>Настоящая политика обработки персональных данных составлена в соответствии с требованиями Федерального закона от 27.07.2006 №152-ФЗ «О персональных данных» и определяет порядок обработки персональных данных и меры по обеспечению безопасности персональных данных, предпринимаемые ИП Хомич Анастасия Алексеевна (ИНН 345951074341).</p>
          <p>1.1. Оператор ставит своей важнейшей целью и условием осуществления своей деятельности соблюдение прав и свобод человека и гражданина при обработке его персональных данных, в том числе защиты прав на неприкосновенность частной жизни, личную и семейную тайну.</p>
          <p>1.2. Настоящая Политика применяется ко всей информации, которую Оператор может получить о посетителях веб-сайта aziatochka.ru.</p>
        </>
      ),
    },
    {
      title: '2. Основные понятия',
      content: (
        <>
          <p><strong>Автоматизированная обработка персональных данных</strong> — обработка персональных данных с помощью средств вычислительной техники.</p>
          <p><strong>Блокирование персональных данных</strong> — временное прекращение обработки персональных данных.</p>
          <p><strong>Веб-сайт</strong> — совокупность графических и информационных материалов, доступных по адресу aziatochka.ru.</p>
          <p><strong>Обработка персональных данных</strong> — любое действие с персональными данными, включая сбор, запись, систематизацию, накопление, хранение, уточнение, извлечение, использование, передачу, обезличивание, блокирование, удаление, уничтожение.</p>
          <p><strong>Персональные данные</strong> — любая информация, относящаяся прямо или косвенно к определённому Пользователю веб-сайта.</p>
          <p><strong>Пользователь</strong> — любой посетитель веб-сайта aziatochka.ru.</p>
        </>
      ),
    },
    {
      title: '3. Права и обязанности Оператора',
      content: (
        <>
          <p className="font-medium text-[var(--text-primary)]">Оператор имеет право:</p>
          <BulletList items={[
            'Получать от субъекта персональных данных достоверную информацию и/или документы',
            'Продолжить обработку персональных данных без согласия субъекта при наличии оснований, указанных в Законе',
            'Самостоятельно определять состав и перечень мер для обеспечения выполнения обязанностей',
          ]} />
          <p className="font-medium text-[var(--text-primary)] mt-4">Оператор обязан:</p>
          <BulletList items={[
            'Предоставлять субъекту информацию, касающуюся обработки его персональных данных',
            'Организовывать обработку в порядке, установленном законодательством РФ',
            'Отвечать на обращения и запросы субъектов персональных данных',
            'Публиковать настоящую Политику в свободном доступе',
            'Принимать правовые, организационные и технические меры для защиты персональных данных',
          ]} />
        </>
      ),
    },
    {
      title: '4. Права и обязанности субъектов персональных данных',
      content: (
        <>
          <p className="font-medium text-[var(--text-primary)]">Субъекты имеют право:</p>
          <BulletList items={[
            'Получать информацию, касающуюся обработки персональных данных',
            'Требовать уточнения, блокирования или уничтожения персональных данных',
            'На отзыв согласия на обработку персональных данных',
            'Обжаловать неправомерные действия Оператора в судебном порядке',
          ]} />
          <p className="font-medium text-[var(--text-primary)] mt-4">Субъекты обязаны:</p>
          <BulletList items={[
            'Предоставлять Оператору достоверные данные о себе',
            'Сообщать об уточнении (обновлении, изменении) своих персональных данных',
          ]} />
        </>
      ),
    },
    {
      title: '5. Обрабатываемые персональные данные',
      content: (
        <>
          <p>5.1. Фамилия, имя, отчество.</p>
          <p>5.2. Номера телефонов.</p>
          <p>5.3. Обезличенные данные о посетителях (файлы «cookie») с помощью сервисов интернет-статистики (Яндекс Метрика и др.).</p>
          <p>5.4. Обработка специальных категорий персональных данных, касающихся расовой, национальной принадлежности, политических взглядов, религиозных убеждений, интимной жизни, Оператором не осуществляется.</p>
        </>
      ),
    },
    {
      title: '6. Принципы обработки',
      content: (
        <BulletList items={[
          'Обработка осуществляется на законной и справедливой основе',
          'Обработка ограничивается достижением конкретных, заранее определённых и законных целей',
          'Не допускается объединение баз данных с несовместимыми целями обработки',
          'Обработке подлежат только данные, отвечающие целям их обработки',
          'При обработке обеспечивается точность и актуальность персональных данных',
          'Хранение осуществляется не дольше, чем требуют цели обработки',
        ]} />
      ),
    },
    {
      title: '7. Цели обработки',
      content: (
        <>
          <BulletList items={[
            'Заключение, исполнение и прекращение гражданско-правовых договоров',
            'Предоставление доступа к сервисам и материалам сайта aziatochka.ru',
          ]} />
          <p className="mt-3">Оператор имеет право направлять уведомления о новых продуктах и специальных предложениях. Пользователь может отказаться от рассылки, направив письмо на aziabox@ingox.ru.</p>
        </>
      ),
    },
    {
      title: '8. Правовые основания обработки',
      content: (
        <BulletList items={[
          'Федеральный закон «Об информации, информационных технологиях и о защите информации» от 27.07.2006 N 149-ФЗ',
          'Уставные документы Оператора',
          'Договоры между оператором и субъектом персональных данных',
          'Согласия Пользователей на обработку их персональных данных',
        ]} />
      ),
    },
    {
      title: '9. Условия обработки',
      content: (
        <BulletList items={[
          'Обработка осуществляется с согласия субъекта персональных данных',
          'Обработка необходима для достижения целей, предусмотренных законом',
          'Обработка необходима для исполнения договора, стороной которого является субъект',
          'Обработка необходима для осуществления прав и законных интересов оператора',
        ]} />
      ),
    },
    {
      title: '10. Порядок сбора, хранения и передачи',
      content: (
        <>
          <p>Безопасность персональных данных обеспечивается путём реализации правовых, организационных и технических мер.</p>
          <p>Персональные данные Пользователя никогда не будут переданы третьим лицам, за исключением случаев, связанных с исполнением действующего законодательства.</p>
          <p>Пользователь может в любой момент отозвать согласие на обработку, направив уведомление на aziabox@ingox.ru.</p>
        </>
      ),
    },
    {
      title: '11. Конфиденциальность',
      content: (
        <p>Оператор и иные лица, получившие доступ к персональным данным, обязаны не раскрывать третьим лицам и не распространять персональные данные без согласия субъекта, если иное не предусмотрено федеральным законом.</p>
      ),
    },
    {
      title: '12. Заключительные положения',
      content: (
        <>
          <p>Пользователь может получить разъяснения по вопросам обработки персональных данных, обратившись по адресу aziabox@ingox.ru.</p>
          <p>В данном документе будут отражены любые изменения политики. Политика действует бессрочно до замены новой версией.</p>
          <p>Актуальная версия расположена по адресу aziatochka.ru/privacy.</p>
        </>
      ),
    },
  ];

  return (
    <LegalPageLayout
      title="Политика конфиденциальности"
      icon={<ShieldCheck className="w-3.5 h-3.5 text-[var(--primary)]" weight="fill" />}
      sections={sections}
      breadcrumbs={[
        { name: 'Главная', href: '/' },
        { name: 'Политика конфиденциальности', href: '/privacy' },
      ]}
      contactBlock={
        <div className="text-sm text-[var(--text-secondary)]">
          <p>Вопросы по обработке персональных данных:</p>
          <a href="mailto:aziabox@ingox.ru" className="text-[var(--primary)] font-medium hover:underline">aziabox@ingox.ru</a>
        </div>
      }
    />
  );
}
